// Shared render helpers for AppBarHeader variants (lint split).
import { openFeedbackMail } from '@/utils/feedbackMail';
import {
  Box,
  IconButton,
  Avatar,
  Drawer,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import MailIcon from '@mui/icons-material/Mail';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import QuickBar from '@/components/layout/QuickBar';
import FilterBar from '@/components/layout/FilterBar';
import DevTierSwitcher from '@/components/layout/DevTierSwitcher';
import AccountMenuLanguagePreference from '@/features/settings/components/AccountMenuLanguagePreference';

export function renderAppBarLogo(alt, sx) {
  return (
    <Box component="img" src="/logo-todoes.svg" alt={alt} sx={{ height: 24, ...sx }} />
  );
}

export function renderAppBarAccountMenu({
  initials,
  avatarAnchor,
  setAvatarAnchor,
  onLogout,
  onOpenHelp,
  onOpenSettings,
  accountMenuTriggerRef,
  accountMenuLabel,
  settingsLabel,
  helpLabel,
  feedbackLabel,
  logoutLabel,
}) {
  return (
    <>
      <IconButton
        ref={accountMenuTriggerRef}
        size="small"
        onClick={(e) => setAvatarAnchor(e.currentTarget)}
        aria-label={accountMenuLabel}
        aria-haspopup="menu"
        aria-expanded={Boolean(avatarAnchor)}
        sx={{ p: 0.25 }}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: 13,
            bgcolor: 'neutral.dark',
          }}
        >
          {initials || '?'}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={avatarAnchor}
        open={Boolean(avatarAnchor)}
        onClose={() => setAvatarAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={onOpenSettings}>
          <ListItemIcon>
            <SettingsOutlinedIcon aria-hidden="true" fontSize="small" />
          </ListItemIcon>
          {settingsLabel}
        </MenuItem>
        <AccountMenuLanguagePreference />
        <MenuItem onClick={onOpenHelp}>
          <ListItemIcon>
            <HelpOutlineIcon aria-hidden="true" fontSize="small" />
          </ListItemIcon>
          {helpLabel}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAvatarAnchor(null);
            openFeedbackMail();
          }}
        >
          <ListItemIcon>
            <FeedbackOutlinedIcon aria-hidden="true" fontSize="small" />
          </ListItemIcon>
          {feedbackLabel}
        </MenuItem>
        <MenuItem onClick={onLogout}>
          <ListItemIcon>
            <LogoutOutlinedIcon aria-hidden="true" fontSize="small" />
          </ListItemIcon>
          {logoutLabel}
        </MenuItem>
        <DevTierSwitcher onSelect={() => setAvatarAnchor(null)} />
      </Menu>
    </>
  );
}

export function renderAppBarBattlefieldActions({
  hasFilter,
  inboxCount,
  onOpenFilter,
  onOpenInbox,
  filterLabel,
  inboxLabel,
  showFilter = true,
}) {
  return (
    <>
      {showFilter ? (
        <IconButton
          size="small"
          onClick={onOpenFilter}
          color={hasFilter ? 'primary' : 'default'}
          aria-label={filterLabel}
        >
          <FilterListIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : null}
      <IconButton size="small" onClick={onOpenInbox} aria-label={inboxLabel}>
        <Badge badgeContent={inboxCount} color="primary" invisible={inboxCount === 0}>
          <MailIcon sx={{ fontSize: 18 }} />
        </Badge>
      </IconButton>
    </>
  );
}

export function renderAppBarQuickFilterRow(sx) {
  return (
    <Box
      data-testid="quick-filter-row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minWidth: 0,
        ...sx,
      }}
    >
      <QuickBar />
      <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
        <FilterBar />
      </Box>
    </Box>
  );
}

export function renderAppBarFilterDrawer(open, onClose) {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxHeight: '60vh',
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <FilterBar />
      </Box>
    </Drawer>
  );
}
